import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, ScaleControl, ImageOverlay, Marker, Tooltip } from "react-leaflet";
import type { LatLngBoundsExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./Environmental.css";
import {
    LEGEND_BAR_HEIGHT,
    declutterTopPositions,
    formatEasternTimestamp,
    countyValueIcon,
    CountyBordersLayer,
    shouldShowDecimals,
    formatMapValue,
} from "./env_util";

// Assets
import tempPic from './assets/temperature-card.png'
import humidityPic from './assets/humidity-card.png'
import windPic from './assets/wind-speed-card.png'
import preassurePic from './assets/preassure-card.png'
import solarPic from './assets/solar-radiation-card.png'
import mrmsPic from './assets/mrms-card.png'
import ercPic from './assets/erc-card.png'
import burningIndexPic from './assets/burning-index-card.png'
import spreadPic from './assets/spread-card.png'
import ignitionPic from './assets/ignition-card.png'
import oneDeadPic from './assets/one-dead-fuel-card.png'
import tenDeadPic from './assets/ten-dead-fuel-card.png'
import hundredDeadPic from './assets/hundred-dead-fuel-card.png'
import thousnadDeadPic from './assets/thousand-dead-fuel-card.png'
import herbaceousPic from './assets/herbaceous-card.png'
import woodyPic from './assets/woody-card.png'
import growingPic from './assets/growing-card.png'
import kbdiPic from './assets/kbdi-card.png'
import liveRiskPic from './assets/live-risk-card.png'


type VariableKey =
    | "temp" | "humid" | "wspd" | "slp" | "sol" | "precip1" | "precip2"
    | "dmc_1_hr" | "dmc_10_hr" | "dmc_100_hr" | "dmc_1000_hr"
    | "lmc_herb" | "lmc_woody"
    | "burning_index" | "energy_release_component" | "spread_component"
    | "ignition_component" | "growing_season_index" | "kb_drought_index"
    | "fire_danger_index";

type ApiGroup = "environmental" | "indices";

interface VariableConfig {
    key: VariableKey;
    group: ApiGroup;
    label: string;
    shortDescription: string;
    aboutTitle: string;
    aboutDescription: string;
    image?: string;
}

const VARIABLES: VariableConfig[] = [
    // --- weather ---
    {
        key: "temp",
        group: "environmental",
        label: "24-hour Temperature Variation",
        shortDescription: "Current air temperature across Florida",
        aboutTitle: "About the Air Temperature Map",
        aboutDescription:
            "The Air Temperature map plots the air temperature (°F) at the standard height of 1.5 m (5 feet) across Florida. Warmer areas are shaded in red and orange tones, while cooler areas appear in blue. Use the hour slider to step through the day and see how temperatures shift from morning through evening.",
        image: tempPic,
    },
    {
        key: "humid",
        group: "environmental",
        label: "24-hour Relative Humidity Variation",
        shortDescription: "Relative humidity levels statewide",
        aboutTitle: "About the Humidity Map",
        aboutDescription:
            "The Humidity map plots relative humidity (%) across Florida, showing how much moisture is in the air relative to what it can hold at the current temperature. Darker blue areas indicate higher humidity, which is common in Florida due to its coastal, subtropical climate. This is useful for tracking muggy conditions, fog potential, and comfort levels throughout the day.",
        image: humidityPic,
    },
    {
        key: "wspd",
        group: "environmental",
        label: "24-hour Wind Speed and Direction",
        shortDescription: "Surface wind speed and direction",
        aboutTitle: "About the Wind Speed Map",
        aboutDescription:
            "The Wind Speed map plots surface wind speed (mph) across Florida. Brighter yellow-green areas indicate stronger winds, while darker purple areas indicate calmer conditions. Arrows show wind direction, pointing the way the wind is blowing. This map is helpful for tracking sea breeze fronts, approaching weather systems, and general wind patterns across the state throughout the day.",
        image: windPic,
    },
    {
        key: "slp",
        group: "environmental",
        label: "24-hour Sea Level Pressure Variation",
        shortDescription: "Sea level pressure across Florida",
        aboutTitle: "About the Sea Level Pressure Map",
        aboutDescription:
            "The Sea Level Pressure map plots barometric pressure (mb), interpolated from ASOS station observations, adjusted to sea level. Pressure gradients drive wind and can signal approaching fronts or storm systems — tighter gradients (rapid color transitions) generally indicate stronger winds, while broad, flat areas indicate calmer, more stable conditions.",
        image: preassurePic,
    },
    {
        key: "sol",
        group: "environmental",
        label: "24-hour Solar Radiation Variation",
        shortDescription: "Incoming solar radiation statewide",
        aboutTitle: "About the Solar Radiation Map",
        aboutDescription:
            "The Solar Radiation map plots incoming shortwave solar radiation (W/m²) across Florida, interpolated from FAWN and ASOS station observations. Brighter areas indicate stronger sunlight, useful for tracking cloud cover, fire weather potential, and diurnal heating patterns throughout the day.",
        image: solarPic,
    },
    {
        key: "precip1",
        group: "environmental",
        label: "24-hour Precipitation (Pass 1)",
        shortDescription: "MRMS multi-sensor precipitation, first pass",
        aboutTitle: "About the Precipitation (Pass 1) Map",
        aboutDescription:
            "The Precipitation Pass 1 map plots hourly rainfall rate (mm/hr) from NOAA's Multi-Sensor QPE product. This is the initial, near-real-time precipitation estimate for each hour, combining radar and gauge data as soon as it becomes available.",
        image: mrmsPic,
    },
    {
        key: "precip2",
        group: "environmental",
        label: "24-hour Precipitation (Pass 2)",
        shortDescription: "MRMS multi-sensor precipitation, refined pass",
        aboutTitle: "About the Precipitation (Pass 2) Map",
        aboutDescription:
            "The Precipitation Pass 2 map plots a refined hourly rainfall rate (mm/hr) from NOAA's Multi-Sensor QPE product, incorporating additional gauge data that arrives after the initial pass. Because this refinement lags by about an hour, the most recent hour may not yet have a Pass 2 value available.",
        image: mrmsPic,
    },
    {
        key: "fire_danger_index",
        group: "indices",
        label: "Live Risk Map",
        shortDescription: "Overall fire danger class (1-5)",
        aboutTitle: "About the Live Risk Map",
        aboutDescription:
            "The Live Risk Map combines the Energy Release Component and relative humidity into a single 1-5 class, following the Florida Forest Service's regional lookup tables. Colors match the official FDAC fire danger scale.",
        image: liveRiskPic,
    },
    {
        key: "energy_release_component",
        group: "indices",
        label: "Energy Release Component",
        shortDescription: "Potential energy release per unit area",
        aboutTitle: "About the Energy Release Component",
        aboutDescription:
            "The Energy Release Component (ERC) estimates the potential energy release per unit area in the flaming front of a fire, based on the combined fuel moisture of all dead and live fuel classes.",
        image: ercPic,
    },
    {
        key: "burning_index",
        group: "indices",
        label: "Burning Index",
        shortDescription: "Relative fire intensity potential",
        aboutTitle: "About the Burning Index",
        aboutDescription:
            "The Burning Index combines spread and energy release potential into a single index representing the effort required to contain a fire.",
        image: burningIndexPic,
    },
    {
        key: "spread_component",
        group: "indices",
        label: "Spread Component",
        shortDescription: "Potential rate of fire spread",
        aboutTitle: "About the Spread Component",
        aboutDescription:
            "The Spread Component estimates the potential forward rate of spread of a fire, based on fuel moisture, wind, and slope.",
        image: spreadPic,
    },
    {
        key: "ignition_component",
        group: "indices",
        label: "Ignition Component",
        shortDescription: "Probability a firebrand causes ignition",
        aboutTitle: "About the Ignition Component",
        aboutDescription:
            "The Ignition Component estimates the probability that a firebrand will cause an ignition requiring suppression action, on a 0-100 scale.",
        image: ignitionPic,
    },
    {
        key: "dmc_1_hr",
        group: "indices",
        label: "1-Hour Dead Fuel Moisture",
        shortDescription: "Fine dead fuel moisture content",
        aboutTitle: "About 1-Hour Dead Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of fine dead fuels (roughly 0-1/4\" diameter) that respond to changes in weather within about an hour.",
        image: oneDeadPic,
    },
    {
        key: "dmc_10_hr",
        group: "indices",
        label: "10-Hour Dead Fuel Moisture",
        shortDescription: "Small dead fuel moisture content",
        aboutTitle: "About 10-Hour Dead Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of dead fuels roughly 1/4\"-1\" in diameter, which respond to weather changes over about 10 hours.",
        image: tenDeadPic,
    },
    {
        key: "dmc_100_hr",
        group: "indices",
        label: "100-Hour Dead Fuel Moisture",
        shortDescription: "Medium dead fuel moisture content",
        aboutTitle: "About 100-Hour Dead Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of dead fuels roughly 1\"-3\" in diameter, which respond to weather changes over about 100 hours.",
        image: hundredDeadPic,
    },
    {
        key: "dmc_1000_hr",
        group: "indices",
        label: "1000-Hour Dead Fuel Moisture",
        shortDescription: "Large dead fuel moisture content",
        aboutTitle: "About 1000-Hour Dead Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of dead fuels roughly 3\"-8\" in diameter, which respond to weather changes over about 1000 hours (~6 weeks).",
        image: thousnadDeadPic,
    },
    {
        key: "lmc_herb",
        group: "indices",
        label: "Herbaceous Live Fuel Moisture",
        shortDescription: "Live herbaceous fuel moisture content",
        aboutTitle: "About Herbaceous Live Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of live herbaceous vegetation (grasses, forbs), which tracks seasonal growth stage as well as short-term drought stress. Note: unlike relative humidity, live fuel moisture is measured on a dry-weight basis and can regularly exceed 100%.",
        image: herbaceousPic,
    },
    {
        key: "lmc_woody",
        group: "indices",
        label: "Woody Live Fuel Moisture",
        shortDescription: "Live woody fuel moisture content",
        aboutTitle: "About Woody Live Fuel Moisture",
        aboutDescription:
            "Estimated moisture content of live woody vegetation (shrubs, brush), which changes more slowly than herbaceous fuels through the season. Note: unlike relative humidity, live fuel moisture is measured on a dry-weight basis and can regularly exceed 100%.",
        image: woodyPic,
    },
    {
        key: "growing_season_index",
        group: "indices",
        label: "Growing Season Index",
        shortDescription: "Seasonal vegetation greenness factor",
        aboutTitle: "About the Growing Season Index",
        aboutDescription:
            "The Growing Season Index reflects the seasonal greenness/growth stage of vegetation, used as an input to the live fuel moisture calculations.",
        image: growingPic,
    },
    {
        key: "kb_drought_index",
        group: "indices",
        label: "Keetch-Byram Drought Index",
        shortDescription: "Cumulative soil moisture deficit",
        aboutTitle: "About the Keetch-Byram Drought Index",
        aboutDescription:
            "The KBDI is a cumulative measure (0-800) of soil moisture deficit, where higher values indicate drier conditions and greater fire risk.",
        image: kbdiPic,
    },
];

interface LegendBin {
    from: number;
    to: number;
    color: string;
    label?: string;
}

interface WindVector {
    lat: number;
    lon: number;
    direction: number;
    speed: number | null;
}

interface VariableDataResponse {
    variable: string;
    time_index: number;
    timestamp: string;
    unit: string;
    min: number | null;
    max: number | null;
    legend_levels: number[];
    legend_bins: LegendBin[];
    bounds: LatLngBoundsExpression;
    county_values: CountyValue[];
    image: string;
}

interface WindDataResponse {
    time_index: number;
    timestamp: string;
    unit: string;
    vectors: WindVector[];
}

interface CountyValue {
    name: string;
    lat: number;
    lon: number;
    value: number;
}

interface MetaResponse {
    dimensions: { time?: number;[key: string]: any };
    coordinates: { name: string; min?: number; max?: number }[];
}

const DEFAULT_CENTER: [number, number] = [27.8, -82.5];

const windIconCache = new Map<number, L.DivIcon>();
const windArrowIcon = (direction: number): L.DivIcon => {
    const bucket = Math.round(direction / 5) * 5 % 360;
    const cached = windIconCache.get(bucket);
    if (cached) return cached;

    const rotation = (bucket + 180) % 360;
    const icon = L.divIcon({
        className: "wind-arrow-icon",
        html: `<svg width="16" height="16" viewBox="0 0 24 24" style="transform: rotate(${rotation}deg);">
                 <path d="M12 3 L12 21 M12 3 L6 10 M12 3 L18 10" stroke="#1f2937" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
               </svg>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });
    windIconCache.set(bucket, icon);
    return icon;
};

const API_BASE: Record<ApiGroup, string> = {
    environmental: "/api/environmental",
    indices: "/api/indices",
};

const Environmental = () => {
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [timeSteps, setTimeSteps] = useState(24);
    const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);

    const [activeTab, setActiveTab] = useState<VariableKey | null>(null);
    const [timeIndex, setTimeIndex] = useState(0);

    const [loadingData, setLoadingData] = useState(false);
    const [currentData, setCurrentData] = useState<VariableDataResponse | null>(null);
    const [windData, setWindData] = useState<WindDataResponse | null>(null);

    const [showWindArrows, setShowWindArrows] = useState(true);
    const [showCounties, setShowCounties] = useState(true);
    const [showCountyValues, setShowCountyValues] = useState(true);

    const cache = useRef<Map<string, VariableDataResponse>>(new Map());
    const windCache = useRef<Map<number, WindDataResponse>>(new Map());
    const metaCache = useRef<Map<ApiGroup, MetaResponse>>(new Map());

    const loadMetaFor = useCallback(async (group: ApiGroup) => {
        const cached = metaCache.current.get(group);
        if (cached) {
            setTimeSteps(cached.dimensions.time ?? 24);
            const lat = cached.coordinates.find((c) => c.name === "lat");
            const lon = cached.coordinates.find((c) => c.name === "lon");
            if (lat?.min != null && lat?.max != null && lon?.min != null && lon?.max != null) {
                setMapCenter([(lat.min + lat.max) / 2, (lon.min + lon.max) / 2]);
            }
            return;
        }

        setLoadingMeta(true);
        try {
            const response = await fetch(`${API_BASE[group]}/`, { credentials: "same-origin" });
            const data = await response.json() as MetaResponse;
            metaCache.current.set(group, data);

            setTimeSteps(data.dimensions.time ?? 24);
            const lat = data.coordinates.find((c) => c.name === "lat");
            const lon = data.coordinates.find((c) => c.name === "lon");
            if (lat?.min != null && lat?.max != null && lon?.min != null && lon?.max != null) {
                setMapCenter([(lat.min + lat.max) / 2, (lon.min + lon.max) / 2]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingMeta(false);
        }
    }, []);

    useEffect(() => {
        loadMetaFor("environmental");
    }, [loadMetaFor]);

    const loadVariableData = useCallback(async (config: VariableConfig, index: number) => {
        const cacheKey = `${config.key}-${index}`;
        const cached = cache.current.get(cacheKey);
        if (cached) {
            setCurrentData(cached);
            return;
        }

        setLoadingData(true);
        setCurrentData(null);
        try {
            const response = await fetch(
                `${API_BASE[config.group]}/data/?variable=${config.key}&time_index=${index}`,
                { credentials: "same-origin" }
            );
            const data = await response.json();

            if (!response.ok || !data || !data.image || !data.bounds) {
                setCurrentData(null);
                return;
            }

            const typed = data as VariableDataResponse;
            cache.current.set(cacheKey, typed);
            setCurrentData(typed);
        } catch (err) {
            console.error(err);
            setCurrentData(null);
        } finally {
            setLoadingData(false);
        }
    }, []);

    const loadWindData = useCallback(async (index: number) => {
        const cached = windCache.current.get(index);
        if (cached) {
            setWindData(cached);
            return;
        }
        try {
            const response = await fetch(
                `${API_BASE.environmental}/wind/?time_index=${index}&stride=4`,
                { credentials: "same-origin" }
            );
            const data = await response.json();

            if (!response.ok || !data || !Array.isArray(data.vectors)) {
                setWindData(null);
                return;
            }

            const typed = data as WindDataResponse;
            windCache.current.set(index, typed);
            setWindData(typed);
        } catch (err) {
            console.error(err);
            setWindData(null);
        }
    }, []);

    const activeConfig = VARIABLES.find(v => v.key === activeTab) ?? null;

    useEffect(() => {
        if (activeConfig) {
            loadVariableData(activeConfig, timeIndex);
        }
        if (activeConfig?.key === "wspd") {
            loadWindData(timeIndex);
        } else {
            setWindData(null);
        }
    }, [activeConfig, timeIndex, loadVariableData, loadWindData]);

    const handleSelectVariable = (config: VariableConfig) => {
        setCurrentData(null);
        setWindData(null);
        setTimeIndex(0);
        setActiveTab(config.key);
        loadMetaFor(config.group);
    };

    const handleBackToMenu = () => {
        setActiveTab(null);
        setCurrentData(null);
        setWindData(null);
    };

    const hasRenderableLayer = !!(currentData && currentData.image && currentData.bounds);

    return (
        <div className="customer-directory-body">
            <TeamHeader title="Environmental" />

            <div className="support-container">
                {activeConfig ? (
                    <div className="env-detail-header">
                        <button type="button" className="env-back-button" onClick={handleBackToMenu}>
                            &larr; Back to maps
                        </button>
                        <div>
                            <h1 className="support-page-title">{activeConfig.label}</h1>
                            <p className="support-page-sub">{activeConfig.shortDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="support-page-header">
                        <div>
                            <h1 className="support-page-title">Florida Environmental Data (v2.0)</h1>
                            <p className="support-page-sub">
                                The FNN Florida Environmental Data Platform v1.0 provides real-time and historical wildfire-weather
                                products for Florida, including hourly 1 km × 1 km gridded fields of key environmental variables and
                                NFDRS4 fire danger indices derived from meteorological networks and gridded precipitation sources.
                                The platform supports visualization through the internal FNN Portal, enables access to environmental
                                conditions across time and space, and establishes the foundation for future wildfire risk calculations
                                and HRL model improvement.
                            </p>
                        </div>
                    </div>
                )}

                {activeConfig && loadingMeta ? (
                    <div className="tv-loading">Loading dataset...</div>
                ) : activeConfig ? (
                    <div className="env-map-section">
                        <div className="env-controls-row">
                            <div className="env-time-control">
                                <label htmlFor="time-slider">
                                    Hour {timeIndex}
                                    {currentData?.timestamp ? ` — ${formatEasternTimestamp(currentData.timestamp)}` : ""}
                                </label>
                                <input
                                    id="time-slider"
                                    type="range"
                                    min={0}
                                    max={timeSteps - 1}
                                    value={timeIndex}
                                    onChange={e => setTimeIndex(Number(e.target.value))}
                                />
                            </div>

                            <div className="env-layer-toggles">
                                {activeTab === "wspd" && (
                                    <label className="env-toggle">
                                        <span className="env-toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={showWindArrows}
                                                onChange={e => setShowWindArrows(e.target.checked)}
                                            />
                                            <span className="env-toggle-thumb" />
                                        </span>
                                        Wind arrows
                                    </label>
                                )}

                                <label className="env-toggle">
                                    <span className="env-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={showCounties}
                                            onChange={e => setShowCounties(e.target.checked)}
                                        />
                                        <span className="env-toggle-thumb" />
                                    </span>
                                    Counties
                                </label>

                                <label className="env-toggle">
                                    <span className="env-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={showCountyValues}
                                            onChange={e => setShowCountyValues(e.target.checked)}
                                        />
                                        <span className="env-toggle-thumb" />
                                    </span>
                                    County values
                                </label>
                            </div>
                        </div>

                        <div className="env-map-wrapper">
                            {loadingData && <div className="env-map-loading">Loading layer...</div>}

                            <MapContainer
                                center={mapCenter}
                                zoom={7}
                                scrollWheelZoom={true}
                                className="env-map"
                            >
                                <TileLayer
                                    attribution='&copy; OpenStreetMap contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <ScaleControl position="bottomleft" />

                                {showCounties && <CountyBordersLayer />}

                                {hasRenderableLayer && (
                                    <ImageOverlay
                                        key={`${currentData!.variable}-${currentData!.time_index}`}
                                        url={currentData!.image}
                                        bounds={currentData!.bounds}
                                        opacity={0.75}
                                    />
                                )}

                                {showWindArrows && activeTab === "wspd" && windData?.vectors?.map((v, idx) => (
                                    <Marker
                                        key={`wind-${idx}`}
                                        position={[v.lat, v.lon]}
                                        icon={windArrowIcon(v.direction)}
                                    >
                                        <Tooltip direction="top" offset={[0, -6]}>
                                            {v.direction}° {v.speed != null ? `· ${v.speed} mph` : ""}
                                        </Tooltip>
                                    </Marker>
                                ))}

                                {showCountyValues && hasRenderableLayer && currentData?.county_values?.map((c, idx) => {
                                    // CHANGE: dynamic precision instead of hardcoded Math.round —
                                    // fixes Growing Season Index (values ~0.45-0.77) collapsing to "1"
                                    const decimals = shouldShowDecimals(currentData.min, currentData.max);
                                    const label = activeTab === "precip1" || activeTab === "precip2"
                                        ? c.value.toFixed(1)
                                        : activeTab === "fire_danger_index"
                                            ? Math.round(c.value).toString()
                                            : formatMapValue(c.value, decimals);
                                    return (
                                        <Marker
                                            key={`cv-${c.name}-${idx}`}
                                            position={[c.lat, c.lon]}
                                            icon={countyValueIcon(label)}
                                        >
                                            <Tooltip direction="top" offset={[0, -10]}>
                                                {c.name} County: {c.value}{currentData.unit}
                                            </Tooltip>
                                        </Marker>
                                    );
                                })}
                            </MapContainer>

                            {!loadingData && !hasRenderableLayer && (
                                <div className="env-no-data-note">
                                    No data available for this hour yet
                                    {activeTab === "precip1" || activeTab === "precip2"
                                        ? " — MRMS precipitation can lag the live feed by about an hour."
                                        : "."}
                                    {" "}Try a different hour on the slider.
                                </div>
                            )}

                            {hasRenderableLayer && (() => {
                                const bins = currentData!.legend_bins ?? [];
                                const decimals = shouldShowDecimals(currentData!.min, currentData!.max);
                                // CHANGE: discrete class bins (e.g. fire_danger_index: from === to, has a label)
                                // need one label per bin, centered — the boundary-based approach below assumes
                                // continuous ranges and produces a spurious duplicate edge label for these.
                                const isDiscrete = bins.length > 0 && bins.every(b => b.from === b.to);

                                if (isDiscrete) {
                                    const reversedBins = [...bins].reverse();
                                    return (
                                        <div className="env-legend env-legend-wide">  {/* CHANGE: added env-legend-wide */}
                                            <div className="env-legend-unit">{currentData!.unit || "class"}</div>
                                            <div className="env-legend-row">
                                                <div className="env-legend-bar">
                                                    {reversedBins.map((bin, idx) => {
                                                        const binHeight = 100 / bins.length;
                                                        return (
                                                            <div
                                                                key={idx}
                                                                className="env-legend-bin"
                                                                style={{
                                                                    top: `${idx * binHeight}%`,
                                                                    height: `${binHeight}%`,
                                                                    backgroundColor: bin.color,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                                <div className="env-legend-labels">
                                                    {reversedBins.map((bin, idx) => {
                                                        const binHeight = 100 / bins.length;
                                                        const centerPx = ((idx * binHeight + binHeight / 2) / 100) * LEGEND_BAR_HEIGHT;
                                                        return (
                                                            <span
                                                                key={idx}
                                                                className="env-legend-label"
                                                                style={{ top: `${centerPx}px` }}
                                                            >
                                                                {bin.label ?? bin.from}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                const boundaries = bins.length
                                    ? [bins[0].from, ...bins.map(b => b.to)].reverse()
                                    : [];
                                const truePct = boundaries.map((_, idx) => {
                                    return (idx / (boundaries.length - 1)) * 100;
                                });
                                const labelPx = declutterTopPositions(truePct);

                                return (
                                    <div className="env-legend">
                                        <div className="env-legend-unit">{currentData!.unit}</div>
                                        <div className="env-legend-row">
                                            <div className="env-legend-bar">
                                                {[...bins].reverse().map((bin, idx) => {
                                                    const binHeight = 100 / bins.length;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="env-legend-bin"
                                                            style={{
                                                                top: `${idx * binHeight}%`,
                                                                height: `${binHeight}%`,
                                                                backgroundColor: bin.color,
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                            <div className="env-legend-labels">
                                                {boundaries.map((v, idx) => {
                                                    const truePx = (truePct[idx] / 100) * LEGEND_BAR_HEIGHT;
                                                    const labelYPx = labelPx[idx];
                                                    const needsLeader = Math.abs(labelYPx - truePx) > 2;
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            {needsLeader && (
                                                                <span
                                                                    className="env-legend-leader"
                                                                    style={{ top: `${truePx}px` }}
                                                                />
                                                            )}
                                                            <span
                                                                className="env-legend-label"
                                                                style={{ top: `${labelYPx}px` }}
                                                            >
                                                                {activeTab === "precip1" || activeTab === "precip2"
                                                                    ? v
                                                                    : formatMapValue(v, decimals)}
                                                            </span>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="env-about">
                            <h2 className="env-about-title">{activeConfig.aboutTitle}</h2>
                            <p className="env-about-desc">{activeConfig.aboutDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="env-card-grid">
                        {VARIABLES.map(variable => (
                            <button
                                key={variable.key}
                                type="button"
                                className="env-card"
                                onClick={() => handleSelectVariable(variable)}
                            >
                                {variable.image && (
                                    <div className="env-card-image-wrapper">
                                        <img src={variable.image} alt={variable.label} className="env-card-image" />
                                    </div>
                                )}
                                <div className="env-card-body">
                                    <h3 className="env-card-title">{variable.label}</h3>
                                    <p className="env-card-desc">{variable.shortDescription}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Environmental;